/**
 * The module's assessment components, and the shared ones it feeds.
 *
 * @module components/studio/panels/AssessmentsPanel
 */

import React from "react";

import { MimloReference } from "@/components/studio/MimloReference";
import { AssessmentRow } from "@/components/studio/panels/AssessmentRow";
import { IncomingAssessments } from "@/components/studio/panels/IncomingAssessments";
import type { ModuleEditorState } from "@/components/studio/useModuleEditor";
import {
  Badge,
  Button,
  EmptyState,
  Icon,
  Panel,
  useEditLock,
  useFocusOnAdd,
} from "@/components/ui";
import type { ModuleWorkspaceRecord } from "@/platform";
import type { MIMLO, Module, ModuleAssessment } from "@/types";
import { isBalanced } from "@/utils/assessment-weight";

export function AssessmentsPanel({
  assessments,
  mimlos,
  record,
  programmeModules,
  editor,
}: {
  assessments: readonly ModuleAssessment[];
  mimlos: readonly MIMLO[];
  record: ModuleWorkspaceRecord;
  programmeModules: readonly Module[];
  editor: ModuleEditorState;
}): React.ReactElement {
  const focusOnAdd = useFocusOnAdd();
  // The lock leaves the Add button off, so the empty state gives its reason instead.
  const lock = useEditLock();
  const { assessmentWeight } = editor;
  const balanced = isBalanced(assessmentWeight);
  const over = assessmentWeight > 100;
  const qualifier = over ? ", over 100" : ", not 100";
  const unbalancedTone = over ? "error" : "warning";

  return (
    <Panel
      aria-label="Assessments"
      description="Define assessment components, weighting, and the outcomes each one measures."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            tone={balanced ? "success" : unbalancedTone}
            variant="soft"
            aria-label={`Assessment weighting totals ${assessmentWeight} percent${balanced ? "" : qualifier}`}
          >
            <Icon name={balanced ? "check-circle" : "warning-circle"} aria-hidden />
            {assessmentWeight}% total
          </Badge>
          <Button
            variant="outline"
            icon="plus"
            onClick={() => {
              focusOnAdd(`module-assessment-title-${editor.addAssessment()}`);
            }}
            data-testid="module-workspace-add-assessment"
          >
            Add assessment
          </Button>
        </div>
      }
    >
      <MimloReference
        mimlos={mimlos}
        scope="assessments"
        data-testid="module-assessment-mimlo-reference"
      />
      <IncomingAssessments
        assessments={record.incomingCrossModuleAssessments ?? []}
        mimlos={mimlos}
        versions={record.versions}
      />
      <div className="mt-5 divide-y divide-base-300">
        {assessments.length ? (
          assessments.map((assessment, index) => (
            <AssessmentRow
              key={assessment.id}
              assessment={assessment}
              index={index}
              mimlos={mimlos}
              moduleId={record.module.id}
              programmeModules={programmeModules}
              editor={editor}
            />
          ))
        ) : (
          <EmptyState
            icon="exam"
            title="No assessments yet"
            description={
              lock.locked ? lock.reason : "Add the components this module is assessed by."
            }
          />
        )}
      </div>
    </Panel>
  );
}

export default AssessmentsPanel;
